import React, { createContext, useContext, useState, useEffect } from 'react';

type Language = 'es' | 'en' | 'de' | 'fr' | 'nl' | 'pt';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations = {
  es: {
    // Navigation
    'nav.inicio': 'Inicio',
    'nav.investigacion': 'Investigación y Proyectos',
    'nav.ensayos': 'Ensayos Clínicos',
    'nav.colaboraciones': 'Colaboraciones',
    'nav.voluntariado': 'Voluntariado',
    'nav.eventos': 'Eventos',
    'nav.newsletter': 'Newsletter',
    'nav.contacto': 'Contacto',
    'nav.donar': 'Donar',
    // Buttons
    'button.donate': 'Hacer Donación',
    'button.contact': 'Contactar',
    'button.backToHome': 'Volver al Inicio',
    // Sections
    'section.donation': 'Tu Donación Salva Vidas',
    // Research Projects Page
    'research.title': 'Investigación y Proyectos',
    'research.subtitle': 'Explora nuestros proyectos de investigación en curso, desde estudios preclínicos hasta ensayos clínicos avanzados que están transformando el tratamiento de enfermedades neurológicas.',
    'research.activeProjects': 'Proyectos Activos',
    'research.totalFunding': 'Financiación Total',
    'research.researchers': 'Investigadores',
    'research.diseases': 'Enfermedades',
    'research.progress': 'Progreso',
    'research.mainObjectives': 'Objetivos principales',
    'research.nextMilestone': 'Próximo hito',
    'research.monthlyUpdates': 'Actualizaciones mensuales de progreso disponibles',
    'research.supportTitle': 'Apoya Nuestra Investigación',
    'research.supportDescription': 'Cada donación acelera nuestro progreso hacia tratamientos que pueden cambiar vidas.',
  },
  en: {
    // Navigation
    'nav.inicio': 'Home',
    'nav.investigacion': 'Research & Projects',
    'nav.ensayos': 'Clinical Trials',
    'nav.colaboraciones': 'Collaborations',
    'nav.voluntariado': 'Volunteering',
    'nav.eventos': 'Events',
    'nav.newsletter': 'Newsletter',
    'nav.contacto': 'Contact',
    'nav.donar': 'Donate',
    // Buttons
    'button.donate': 'Make Donation',
    'button.contact': 'Contact',
    'button.backToHome': 'Back to Home',
    // Sections
    'section.donation': 'Your Donation Saves Lives',
    // Research Projects Page
    'research.title': 'Research and Projects',
    'research.subtitle': 'Explore our ongoing research projects, from preclinical studies to advanced clinical trials that are transforming the treatment of neurological diseases.',
    'research.activeProjects': 'Active Projects',
    'research.totalFunding': 'Total Funding',
    'research.researchers': 'Researchers',
    'research.diseases': 'Diseases',
    'research.progress': 'Progress',
    'research.mainObjectives': 'Main Objectives',
    'research.nextMilestone': 'Next Milestone',
    'research.monthlyUpdates': 'Monthly progress updates available',
    'research.supportTitle': 'Support Our Research',
    'research.supportDescription': 'Every donation accelerates our progress towards life-changing treatments.',
  },
  de: {
    // Navigation
    'nav.inicio': 'Startseite',
    'nav.investigacion': 'Forschung und Projekte',
    'nav.ensayos': 'Klinische Studien',
    'nav.colaboraciones': 'Kooperationen',
    'nav.voluntariado': 'Freiwilligenarbeit',
    'nav.eventos': 'Veranstaltungen',
    'nav.newsletter': 'Newsletter',
    'nav.contacto': 'Kontakt',
    'nav.donar': 'Spenden',
    // Buttons
    'button.donate': 'Spende Machen',
    'button.contact': 'Kontakt',
    'button.backToHome': 'Zurück zur Startseite',
    // Sections
    'section.donation': 'Ihre Spende Rettet Leben',
    // Research Projects Page
    'research.title': 'Forschung und Projekte',
    'research.subtitle': 'Erkunden Sie unsere laufenden Forschungsprojekte, von präklinischen Studien bis hin zu fortgeschrittenen klinischen Versuchen, die die Behandlung neurologischer Erkrankungen revolutionieren.',
    'research.activeProjects': 'Aktive Projekte',
    'research.totalFunding': 'Gesamtfinanzierung',
    'research.researchers': 'Forscher',
    'research.diseases': 'Krankheiten',
    'research.progress': 'Fortschritt',
    'research.mainObjectives': 'Hauptziele',
    'research.nextMilestone': 'Nächster Meilenstein',
    'research.monthlyUpdates': 'Monatliche Fortschrittsupdates verfügbar',
    'research.supportTitle': 'Unterstützen Sie unsere Forschung',
    'research.supportDescription': 'Jede Spende beschleunigt unseren Fortschritt zu lebensverändernden Behandlungen.',
  },
  fr: {
    // Navigation
    'nav.inicio': 'Accueil',
    'nav.investigacion': 'Recherche et Projets',
    'nav.ensayos': 'Essais Cliniques',
    'nav.colaboraciones': 'Collaborations',
    'nav.voluntariado': 'Bénévolat',
    'nav.eventos': 'Événements',
    'nav.newsletter': 'Newsletter',
    'nav.contacto': 'Contact',
    'nav.donar': 'Faire un Don',
    // Buttons
    'button.donate': 'Faire un Don',
    'button.contact': 'Contact',
    'button.backToHome': 'Retour à l\'Accueil',
    // Sections
    'section.donation': 'Votre Don Sauve des Vies',
    // Research Projects Page
    'research.title': 'Recherche et Projets',
    'research.subtitle': 'Explorez nos projets de recherche en cours, des études précliniques aux essais cliniques avancés qui transforment le traitement des maladies neurologiques.',
    'research.activeProjects': 'Projets Actifs',
    'research.totalFunding': 'Financement Total',
    'research.researchers': 'Chercheurs',
    'research.diseases': 'Maladies',
    'research.progress': 'Progrès',
    'research.mainObjectives': 'Objectifs Principaux',
    'research.nextMilestone': 'Prochain Jalon',
    'research.monthlyUpdates': 'Mises à jour mensuelles des progrès disponibles',
    'research.supportTitle': 'Soutenez Notre Recherche',
    'research.supportDescription': 'Chaque don accélère nos progrès vers des traitements qui changent la vie.',
  },
  nl: {
    // Navigation
    'nav.inicio': 'Home',
    'nav.investigacion': 'Onderzoek en Projecten',
    'nav.ensayos': 'Klinische Proeven',
    'nav.colaboraciones': 'Samenwerkingen',
    'nav.voluntariado': 'Vrijwilligerswerk',
    'nav.eventos': 'Evenementen',
    'nav.newsletter': 'Nieuwsbrief',
    'nav.contacto': 'Contact',
    'nav.donar': 'Doneren',
    // Buttons
    'button.donate': 'Donatie Doen',
    'button.contact': 'Contact',
    'button.backToHome': 'Terug naar Home',
    // Sections
    'section.donation': 'Uw Donatie Redt Levens',
    // Research Projects Page
    'research.title': 'Onderzoek en Projecten',
    'research.subtitle': 'Ontdek onze lopende onderzoeksprojecten, van preklinische studies tot geavanceerde klinische proeven die de behandeling van neurologische aandoeningen transformeren.',
    'research.activeProjects': 'Actieve Projecten',
    'research.totalFunding': 'Totale Financiering',
    'research.researchers': 'Onderzoekers',
    'research.diseases': 'Ziekten',
    'research.progress': 'Voortgang',
    'research.mainObjectives': 'Hoofddoelstellingen',
    'research.nextMilestone': 'Volgende Mijlpaal',
    'research.monthlyUpdates': 'Maandelijkse voortgangsupdates beschikbaar',
    'research.supportTitle': 'Steun Ons Onderzoek',
    'research.supportDescription': 'Elke donatie versnelt onze vooruitgang naar levensveranderende behandelingen.',
  },
  pt: {
    // Navigation
    'nav.inicio': 'Início',
    'nav.investigacion': 'Investigação e Projetos',
    'nav.ensayos': 'Ensaios Clínicos',
    'nav.colaboraciones': 'Colaborações',
    'nav.voluntariado': 'Voluntariado',
    'nav.eventos': 'Eventos',
    'nav.newsletter': 'Newsletter',
    'nav.contacto': 'Contato',
    'nav.donar': 'Doar',
    // Buttons
    'button.donate': 'Fazer Doação',
    'button.contact': 'Contato',
    'button.backToHome': 'Voltar ao Início',
    // Sections
    'section.donation': 'A Sua Doação Salva Vidas',
    // Research Projects Page
    'research.title': 'Investigação e Projetos',
    'research.subtitle': 'Explore os nossos projetos de investigação em curso, desde estudos pré-clínicos até ensaios clínicos avançados que estão a transformar o tratamento de doenças neurológicas.',
    'research.activeProjects': 'Projetos Ativos',
    'research.totalFunding': 'Financiamento Total',
    'research.researchers': 'Investigadores',
    'research.diseases': 'Doenças',
    'research.progress': 'Progresso',
    'research.mainObjectives': 'Objetivos Principais',
    'research.nextMilestone': 'Próximo Marco',
    'research.monthlyUpdates': 'Atualizações mensais de progresso disponíveis',
    'research.supportTitle': 'Apoie a Nossa Investigação',
    'research.supportDescription': 'Cada doação acelera o nosso progresso em direção a tratamentos que mudam vidas.',
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('es');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') as Language;
    if (savedLanguage && translations[savedLanguage]) {
      setLanguage(savedLanguage);
    }
  }, []);

  const handleSetLanguage = (lang: Language) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
  };

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};